import { XcoreCore } from "XcoreCore";

/**
 * Used for EPs with `published` query param.
 * Queries made in web should fetch published contents by default if not specified otherwise.
 * Queries made in admin should fetch all contents by default if not specified otherwise.
 *
 * @param publishedOnlyOption `Boolean | undefined`
 */
export const resolveFetchPublishedOnly = (publishedOnlyOption: boolean | undefined, authToken?: string | undefined): { published?: true } =>
  // E.g. in web it can be specified that draft contents should also be fetched
  publishedOnlyOption === false
    ? {}
    : // On web fetch only published contents by default
  // This env is set to `true` in `withXcore.js` wrapper for next config that is only being used on web
    process.env.NEXT_PUBLIC_FETCH_PUBLISHED_ONLY_BY_DEFAULT !== undefined
      ? // If token is passed from preview data allow unpublished content
      authToken
        ? {}
        : // If the env is `undefined` and auth token is not present fetch only published contents
        process.env.NEXT_PUBLIC_FETCH_PUBLISHED_ONLY_BY_DEFAULT === "true"
          ? { published: true }
          : {}
      : publishedOnlyOption
        ? { published: true }
        : {};

/**
 * Resolves locales based on the provided locales and env config.
 * If locales are provided, add fallback locale to the provided locales array.
 * If locales are not provided and site is web, fetch only current and fallback locale.
 * Otherwise, fetch all locales.
 *
 * @param core XcoreCore
 * @param locales (Optional), string[] - locales from locales options
 */
export const resolveLocaleParams = (core: XcoreCore, locales?: string[]): { locales?: string[] } =>
  // If locales are provided, resolve them, otherwise fetch only current and fallback locale
  locales !== undefined
    ? resolveContentLocalesFallback(core, locales)
    : // On web fetch only current and fallback locale by default, otherwise fetch all locales
  // This env is set to `true` in `withXcore.js` wrapper for next config that is only being used on web
    process.env.NEXT_PUBLIC_ADD_LOCALE_PARAMS_TO_QUERIES !== undefined
      ? // eslint-disable-next-line no-extra-boolean-cast
      process.env.NEXT_PUBLIC_ADD_LOCALE_PARAMS_TO_QUERIES === "true"
        ? { locales: core.localeContent }
        : {}
      : {};

/**
 * Adds fallback locale to the provided locales array.
 * As a side effect this function also prevents sending duplicate locales.
 *
 * @param core XcoreCore
 * @param locales string[] - locales from locales options
 */
const resolveContentLocalesFallback = (core: XcoreCore, locales: string[]): { locales?: string[] } => {
  const localesWithFallbacks: string[] = [];

  locales.forEach(
    (locale) =>
      !localesWithFallbacks.includes(locale) &&
      (core.locales[locale]?.fallbackContentLocale
        ? localesWithFallbacks.includes(core.locales[locale]?.fallbackContentLocale as string)
          ? localesWithFallbacks.push(locale)
          : localesWithFallbacks.push(locale, core.locales[locale]?.fallbackContentLocale as string)
        : localesWithFallbacks.push(locale))
  );

  return localesWithFallbacks.length > 0 ? { locales: localesWithFallbacks } : {};
};
